import { APPCHARGE_ERROR_TYPE_TO_KEY } from '@/constants/error';
import { Errors } from '@/constants/errors';
import type { ApiError } from '@/services/apiClient';
import {
  AppchargeErrorType,
  type AdyenPaymentResponse,
  type NuveiPaymentStatusResponse,
  type StripePaymentResponse,
} from '@/types/payment';
import type { VenmoPaymentResponse } from '@/types/venmo';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Resolve a user-facing payment error string from an `AppchargeErrorType`.
 * Falls back to `GENERIC_DECLINE` when the type is absent — matches BE
 * `mapStripeErrorMessage` semantics so providers that don't enrich the
 * failure payload still surface actionable decline copy.
 */
export const getAppchargeErrorMessage = (t: TranslateFn, appChargeErrorType?: AppchargeErrorType): string => {
  const errorType = appChargeErrorType ?? AppchargeErrorType.GENERIC_DECLINE;
  return t(`appchargeErrors.${APPCHARGE_ERROR_TYPE_TO_KEY[errorType]}`);
};

/**
 * Velocity / rate-limit declines: the issuer (or Stripe Radar) is throttling
 * repeated attempts on the same card, not rejecting the card outright. These
 * are recoverable — the shopper succeeds by waiting a moment or switching
 * payment method — so callers surface dedicated "wait or try another method"
 * copy and a dedicated RUM signal instead of treating them as a hard decline.
 */
const VELOCITY_DECLINE_TYPES: ReadonlySet<AppchargeErrorType> = new Set([
  AppchargeErrorType.CARD_VELOCITY_EXCEEDED,
  AppchargeErrorType.WITHDRAWAL_COUNT_LIMIT_EXCEEDED,
]);

export const isVelocityDecline = (appChargeErrorType?: AppchargeErrorType): boolean =>
  appChargeErrorType !== undefined && VELOCITY_DECLINE_TYPES.has(appChargeErrorType);

const rawText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/**
 * The provider-response fields that can carry failure text. Derived from the
 * response interfaces rather than restated structurally, so renaming a field
 * on one of them fails the build here instead of silently returning
 * `undefined` at runtime. A response only ever populates its own provider's
 * key, hence `Partial`.
 */
type PaymentFailureSource = Partial<
  Pick<StripePaymentResponse, 'stripe'> &
    Pick<AdyenPaymentResponse, 'adyen'> &
    Pick<VenmoPaymentResponse, 'braintree'> &
    Pick<NuveiPaymentStatusResponse, 'gwErrorReason' | 'reason'>
>;

/**
 * Verbatim PSP failure text off a finalize-payment / status response, if any.
 * Each provider parks it somewhere different; Nuvei splits it across two fields.
 */
export const getRawPaymentFailureMessage = (results: unknown): string | undefined => {
  if (!results || typeof results !== 'object') return undefined;

  const candidate = results as PaymentFailureSource;

  return (
    rawText(candidate.stripe?.message) ??
    rawText(candidate.adyen?.message) ??
    rawText(candidate.braintree?.message) ??
    rawText(candidate.gwErrorReason) ??
    rawText(candidate.reason)
  );
};

/**
 * The verbatim message the BE put on a non-2xx body, or `undefined` when it
 * sent none. SDK `reason` carries only such raw text — never localized copy —
 * so publishers can group failures on a locale-independent value.
 */
export const getRawApiErrorMessage = (error: unknown): string | undefined => {
  const apiError = error as ApiError | undefined;
  const errorInfo = apiError?.info;

  if (typeof errorInfo === 'string' && errorInfo.trim()) {
    return errorInfo;
  }

  if (errorInfo && typeof errorInfo === 'object') {
    const message =
      (errorInfo as { message?: string; error?: { message?: string } }).message ||
      (errorInfo as { message?: string; error?: { message?: string } }).error?.message;

    if (message?.trim()) {
      return message;
    }
  }

  return undefined;
};

/**
 * `errorCode` off a parsed non-2xx body, which is the only place it appears —
 * it is a field the gateway writes next to `message`, not the HTTP status.
 * A body carrying no `errorCode` (or one that never parsed as JSON, which
 * `apiClient` hands back as raw text) yields `undefined`.
 */
const readErrorCode = (errorInfo: unknown): string | undefined =>
  errorInfo && typeof errorInfo === 'object' ? rawText((errorInfo as { errorCode?: unknown }).errorCode) : undefined;

/**
 * The stable `errorCode` checkout-gateway stamps on session failures (see
 * `CheckoutSessionErrorCode`). Purely additive: it rides alongside the
 * existing `reason` rather than replacing it, so publishers can branch on the
 * cause — a 401 alone cannot tell an expired session from a bad token —
 * without any change to the text they already consume.
 *
 * Falls back to one `cause` hop for wrapped failures — `usePaymentToServer`
 * and `usePciPaymentToServer` rethrow as `new Error(..., { cause: apiError })`.
 * No fatal event feeds one of those in today; the hop is there so a caller that
 * starts to cannot silently lose the code. The fallback is on a missing code
 * rather than a missing `info`, so a wrapper carrying its own unrelated body
 * still lets the gateway code underneath through.
 *
 * Returns the raw string rather than narrowing to the union — the gateway may
 * add codes ahead of this constant, and dropping an unknown one would be worse
 * than forwarding it.
 */
export const getApiErrorCode = (error: unknown): string | undefined =>
  readErrorCode((error as ApiError | undefined)?.info) ??
  readErrorCode((error as { cause?: ApiError } | undefined)?.cause?.info);

export const getErrorMessage = (error: unknown, fallbackMessage: string): string =>
  getRawApiErrorMessage(error) ?? fallbackMessage;

export function checkIfServerFinalizeFailure(error: unknown): boolean {
  return error instanceof Error && (error as Error)?.message === Errors.global.failedToSendPayment;
}
